import { CheckApiKey, CheckPolicy, Resource } from '../../framework/permission-util'
import { RT } from '../permissions'
import Executive from '../../framework/rmi-util'
import { register } from '../../framework/rmi-server-util'
import { cleanupAiThreads, translateContent, guessCategories, guessCustomerSegmentTags, generateAiContent, refreshVectorStore, generateAltText, getAltTextBatchClients, listAltTextCandidates, generateAltTextForAsset, type ListCandidatesResult, type AssetResult } from './AiTechnical'
import type { AiIdResult, TranslationRequest, TranslationResponse } from './ai.entities'
import { CleanupResult, Features } from '../contract/util/types'
import { cmClient } from '../../legacy/contentmanager-util'
import { clientHasFeature } from '../contract/util/client'
import { AiRequestType } from './util/external-ai-client'

class I18nString {
	de: string
	[ locale: string ]: string
}

export class GuessCategoryRequest {
	title: I18nString
	shortDescription: I18nString
	longDescription: I18nString
	address: string
}

export class GuessCustomerSegmentTagsRequest {
	title: I18nString
	shortDescription: I18nString
	longDescription: I18nString
	address: string
}

export default class AiExecutive extends Executive {

	initServer() {
	}

	@CheckPolicy()
	async guessCategories(@Resource(RT.CLIENT) clientId: string, request: GuessCategoryRequest): Promise<AiIdResult[]> {
		return await guessCategories(clientId, request)
	}

	@CheckPolicy()
	async guessCustomerSegmentTags(@Resource(RT.CLIENT) clientId: string, request: GuessCustomerSegmentTagsRequest): Promise<AiIdResult[]> {
		return await guessCustomerSegmentTags(clientId, request)
	}

	@CheckPolicy()
	async translate(@Resource(RT.CLIENT) clientId: string, request: TranslationRequest): Promise<TranslationResponse> {
		return await translateContent(clientId, request)
	}

	// Only for testing from Postman - the cronjob will directly call the technical
	@CheckApiKey()
	async cleanupAiThreads(@Resource(RT.CLIENT) clientId: string): Promise<CleanupResult> {
		return await cleanupAiThreads()
	}

	@CheckPolicy()
	async generateAiContent(@Resource(RT.CLIENT) clientId: string, @Resource(RT.PROVIDER) spId: string, request: any, type: AiRequestType): Promise<any> {
		return await generateAiContent(clientId, request, type)
	}

	@CheckPolicy()
	async generateAltText(@Resource(RT.CLIENT) clientId: string, @Resource(RT.PROVIDER) spId: string, imageUrl: string, imageContext?: { title?: string, description?: string }): Promise<{ [locale: string]: string }> {
		const client = await cmClient.getEntry({ content_type: 'client', 'sys.id': clientId, include: 0, select: 'features' })
		if (!clientHasFeature(client, Features.ClientFeatures.AI_ALT_TEXT_GENERATION)) {
			throw new Error('Feature AI-alt-text-generation is not enabled for this client')
		}
		return await generateAltText(clientId, imageUrl, imageContext)
	}

	@CheckApiKey()
	async refreshVectorStore(@Resource(RT.CLIENT) clientId: string, organizationId: string, assistantId: string): Promise<void> {
		await refreshVectorStore(clientId, organizationId, assistantId)
	}

	@CheckApiKey()
	async getAltTextBatchClients(): Promise<{ clientIds: string[] }> {
		return { clientIds: await getAltTextBatchClients() }
	}

	@CheckApiKey()
	async listAltTextCandidates(clientId: string, skip = 0, limit = 50): Promise<ListCandidatesResult> {
		return listAltTextCandidates(clientId, skip, limit)
	}

	@CheckApiKey()
	async generateAltTextForAsset(clientId: string, assetId: string): Promise<AssetResult> {
		return generateAltTextForAsset(clientId, assetId)
	}

	@CheckApiKey()
	async refreshVectorStores(): Promise<void> {
		const clients = await cmClient.getEntries({
			content_type: 'client',
			include: 0,
			select: 'title,features',
		})

		const clientsToRefresh = []
		for (const client of clients) {
			if (clientHasFeature(client, Features.ClientFeatures.AI_PRODUCT_CATEGORIES) || clientHasFeature(client, Features.ClientFeatures.AI_CUSTOMER_SEGMENTS)) {
				clientsToRefresh.push({ clientId: client.sys.id })
			}
		}
		await Promise.all(clientsToRefresh.map(client => refreshVectorStore(client.clientId)))
	}
}

register(AiExecutive)